import React, { useState, useCallback } from "react";
import { isEmpty } from "lodash";
import { Col, Form, Row } from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import { useMutation } from "react-query";
import { components } from "react-select";

import {
	HookFormInputGroup,
	HookSelectAsyncFormGroup,
	HookSelectFormGroup,
	Skeleton,
} from "components";
import { LineDivider } from "components/layout";
import { DebounceTimeout } from "globalConstants";
import { getFullName } from "helpers";
import useLoader from "hooks/useLoader";
import { paymentsService } from "services";
import { MerchantWithholdStatuses } from "types/enums/withholdStatuses";
import {
	float4Normalizer,
	trimMidSentenceNormalizer,
	trimNormalizer,
} from "validators";
import { required } from "validators/validation";

import StatusRow from "./rowComponents/merchantStatusRow";
import OptionLabel from "./rowComponents/optionLabel";

import styles from "../paymentsModal.module.scss";

type WithholdMerchantFormProps = {
	paidMonth: string;
	paidYear: string;
	isEditing?: boolean;
	initialValues?: { [key: string]: any };
};

export const WithholdMerchantForm = ({
	paidMonth,
	paidYear,
	isEditing = false,
	initialValues,
}: WithholdMerchantFormProps) => {
	const {
		register,
		setValue,

		formState: { errors },
	} = useFormContext();

	const [merchantAgents, setMerchantAgents] = useState<{
		[key: string]: any;
	}>([]);

	const { mutate: getMerchantAgents, isLoading: isMerchantAgentsLoading } =
		useMutation(
			(values: { [key: string]: any }) =>
				paymentsService.getMerchantMidName({
					paidMonth,
					paidYear,
					merchant_id: values.id,
				}),
			{
				onSuccess: res => {
					setMerchantAgents(res.items);
				},
			},
		);
	const Loader = useLoader(isMerchantAgentsLoading);

	const loadMerchants = async (pattern, prevOptions, { page }) => {
		try {
			const newMerchants = await paymentsService.getMerchantsForWithhold({
				paidMonth,
				paidYear,
				pattern,
				page,
			});

			const hasMore =
				newMerchants.items.length !== 0 &&
				prevOptions.length + newMerchants.items.length <
					newMerchants.count;

			return {
				options: newMerchants.items,
				hasMore,
				additional: {
					page: page + 1,
				},
			};
		} catch {
			return { options: [] };
		}
	};

	const handleMerchantNameChanged = useCallback(
		value => {
			setValue("merchantValues", value, {
				shouldDirty: true,
			});
			getMerchantAgents(value);
		},
		[setValue, getMerchantAgents],
	);

	const { Option } = components;

	const MerchantOption = props => {
		const { merchant_name, merchant_number, is_withheld } = props.data;
		const value = (
			<div>
				{merchant_name}
				&nbsp;
				<span className="text-light">({merchant_number})</span>
			</div>
		);

		return (
			<Option {...props}>
				<OptionLabel value={value} isDisabled={is_withheld} />
			</Option>
		);
	};

	const SingleValue = props => {
		const { merchant_name, merchant_number } = props.data;
		return (
			<components.SingleValue {...props}>
				{merchant_number} / {merchant_name}
			</components.SingleValue>
		);
	};

	return (
		<>
			{isEditing ? (
				<Form.Group as={Row}>
					<Col>
						<div className={styles.opacityColor}>
							MID/ Merchant Name
						</div>
						<div>
							{initialValues?.merchant?.merchantNumber} /{" "}
							{initialValues?.merchant?.merchantName}
						</div>
					</Col>
				</Form.Group>
			) : (
				<HookSelectAsyncFormGroup
					name="merchantValues"
					label="MID/ Merchant Name"
					getOptionValue={option => option.id}
					loadOptions={loadMerchants}
					additional={{ page: 0 }}
					placeholder="Please Select"
					debounceTimeout={DebounceTimeout}
					blurLoading={!isEmpty(loadMerchants)}
					cacheOptions
					isSearchable
					defaultOptions
					defaultValue={null}
					rules={{
						validate: {
							required,
						},
					}}
					labelSize={{ sm: 12 }}
					controlSize={{ sm: 12 }}
					onChange={handleMerchantNameChanged}
					isOptionDisabled={option => option.is_withheld}
					showRequiredMark
					components={{
						SingleValue,
						Option: MerchantOption,
					}}
				/>
			)}

			{!isEmpty(merchantAgents) && (
				<Row>
					<Col>
						<LineDivider className="mt-2" />
						<p className={styles.opacityColor}>
							The following agents will also have the residual
							related to this merchant withheld:
						</p>

						<Loader
							skeleton={<Skeleton height={34} />}
							component={merchantAgents?.map((item, key) => (
								<Row key={key}>
									<Col className={styles.agentLine}>
										<div>
											<div>
												{getFullName(item?.agent)}
											</div>
											<div
												className={styles.opacityColor}
											>
												Total Residual Payout (before
												adjustments)
											</div>
										</div>
										<div>
											{float4Normalizer(
												item.payout_amount_before_adjustments,
												"$",
											)}
										</div>
									</Col>
								</Row>
							))}
						/>
						<LineDivider />
					</Col>
				</Row>
			)}

			<HookSelectFormGroup
				label="Withhold Reason"
				options={MerchantWithholdStatuses}
				getOptionLabel={option => option.displayName}
				getOptionValue={option => option.value}
				placeholder="Please Select"
				name="withhold_reason"
				isSearchable={false}
				defaultValue={null}
				cacheOptions
				defaultOptions
				debounceTimeout={DebounceTimeout}
				colSelect={{ sm: 12 }}
				rules={[]}
			/>

			<HookFormInputGroup
				name="description"
				register={register}
				label="Comments"
				colInput={12}
				colLabel={12}
				autoComplete="off"
				normalize={trimMidSentenceNormalizer}
				normalizeOnBlur={trimNormalizer}
				errors={errors}
				as="textarea"
				rows={5}
			/>

			<Form.Group as={Row}>
				<Col>
					<StatusRow register={register} title="merchant" />
				</Col>
			</Form.Group>
			<LineDivider className="pt-2" />
		</>
	);
};
